import { useEffect, useRef, useState, type RefObject } from 'react';
import {
  PULL_TRIGGER_DISTANCE,
  readScrollTop,
  resolvePullDistance,
  shouldTriggerRefresh,
} from '../lib/pull-to-refresh';

/** Mantém o indicador visível por um instante para o refresh não “piscar”. */
const MIN_VISIBLE_MS = 450;

/** Folga antes de assumir o gesto, para não roubar a rolagem normal. */
const PULL_SLOP = 8;

interface UsePullToRefreshOptions {
  scrollRef: RefObject<HTMLElement | null>;
  onRefresh: () => Promise<unknown>;
}

/**
 * Áreas com scroll próprio (planta da vaga, corpo dos drawers) ficam com o
 * gesto; só o scroller principal no topo aciona o refresh.
 */
function hasScrollableAncestor(target: EventTarget | null, boundary: HTMLElement): boolean {
  let node = target instanceof Element ? target : null;
  while (node && node !== boundary) {
    const { overflowY } = window.getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return true;
    }
    node = node.parentElement;
  }
  return false;
}

function documentScrollTop(): number {
  return window.scrollY || document.documentElement.scrollTop || 0;
}

export function usePullToRefresh({ scrollRef, onRefresh }: UsePullToRefreshOptions) {
  const [distance, setDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const onRefreshRef = useRef(onRefresh);

  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof window === 'undefined') return;
    if (!window.matchMedia('(pointer: coarse)').matches) return;

    let startY: number | null = null;
    let pulled = 0;
    let refreshing = false;

    /**
     * O conteúdo acompanha o dedo. Escrevemos no style direto, sem estado do
     * React, para não re-renderizar as páginas a cada touchmove. Voltamos a
     * `transform` vazio (e não `translateY(0)`) para o elemento não virar
     * containing block de `position: fixed` fora do gesto.
     */
    const shiftContent = (value: number, animate: boolean) => {
      el.style.transition = animate ? 'transform 220ms ease-out' : '';
      el.style.transform = value > 0 ? `translateY(${value}px)` : '';
    };

    const reset = () => {
      startY = null;
      if (pulled > 0) {
        setDistance(0);
        shiftContent(0, true);
      }
      pulled = 0;
    };

    const onTouchStart = (event: TouchEvent) => {
      reset();
      if (refreshing || event.touches.length !== 1) return;
      if (readScrollTop(el, documentScrollTop()) > 0) return;
      if (hasScrollableAncestor(event.target, el)) return;
      startY = event.touches[0]?.clientY ?? null;
    };

    const onTouchMove = (event: TouchEvent) => {
      if (startY === null || refreshing) return;
      if (event.touches.length !== 1 || readScrollTop(el, documentScrollTop()) > 0) {
        reset();
        return;
      }

      const raw = (event.touches[0]?.clientY ?? startY) - startY;
      // Dedo subindo é rolagem normal: solta o gesto até o próximo toque.
      if (raw <= PULL_SLOP) {
        if (raw < 0) reset();
        return;
      }

      // Sem isto o iOS faz o bounce nativo e arrasta o indicador junto.
      if (event.cancelable) event.preventDefault();
      pulled = resolvePullDistance(raw - PULL_SLOP);
      setDistance(pulled);
      shiftContent(pulled, false);
    };

    const onTouchEnd = () => {
      const reached = startY !== null && shouldTriggerRefresh(pulled);
      if (!reached || refreshing) {
        reset();
        return;
      }

      startY = null;
      pulled = 0;
      setDistance(0);
      refreshing = true;
      setIsRefreshing(true);
      // Conteúdo fica aberto enquanto atualiza, com o spinner no espaço livre.
      shiftContent(PULL_TRIGGER_DISTANCE, true);
      void (async () => {
        const startedAt = Date.now();
        try {
          await onRefreshRef.current();
        } catch {
          // Falha de rede já aparece nas telas; aqui só encerramos o indicador.
        } finally {
          const remaining = MIN_VISIBLE_MS - (Date.now() - startedAt);
          if (remaining > 0) {
            await new Promise((resolve) => window.setTimeout(resolve, remaining));
          }
          refreshing = false;
          setIsRefreshing(false);
          shiftContent(0, true);
        }
      })();
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', reset, { passive: true });

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', reset);
      el.style.transition = '';
      el.style.transform = '';
    };
  }, [scrollRef]);

  return { distance, isRefreshing };
}
